import { Component } from "react"
import { graphql } from '@apollo/client/react/hoc'
import { AllPricingTemplates } from "./Queries"
import { Table } from "reactstrap"
import toTitleCase from "../Functions/toTitleCase"
import "../Material/ListingHeader.css"
import "./ListingTable.css"
import PrimaryListingContainer from "../Material/PrimaryListingContainer"
import EditPricingModal from "./EditPricingModal"
import { StatusChip, VisibilityChip } from "./ListingHelpers"
import { useAdvancedFilters } from "../Filters/filterUrl"
import { combineFilters } from "../Filters/presetFilters"

const tableHeaders = [
    { id: "title", name: "Template", sortable: true },
    { id: "vehicle", name: "Vehicle Eligibility", sortable: false },
    { id: "price", name: "Rate", sortable: false },
    { id: "status", name: "Status", sortable: false },
    { id: "visibleToCustomers", name: "Customer Visibility", sortable: false },
    { id: "action", name: "Action", sortable: false },
]

export class PricingTable extends Component {
    constructor(props) {
        super(props)
        this.state = {
            loading: false,
            openModal: "",
            selectAll: false,
            expandedRows: new Set(),
        }
    }

    toggleModal = (name) => {
        this.setState({ openModal: this.state.openModal === name ? "" : name })
    };
    toggleExpand = (id) => {
        this.setState((prevState) => {
            const expandedRows = new Set(prevState.expandedRows)
            expandedRows.has(id) ? expandedRows.delete(id) : expandedRows.add(id)
            return { expandedRows }
        })
    };
    updateSelected = (value) => {
        let selectedPricing = this.props.selectedPricing
        if (selectedPricing.includes(value)) {
            selectedPricing = selectedPricing.filter(item => item !== value)
        } else {
            selectedPricing.push(value)
        }
        this.props.setSelectedPricing(selectedPricing)
    };
    toggleSelectAllClick = () => {
        if (this.props.drivers && !this.state.selectAll) {
            this.props.setSelectedPricing(this.props.allPricingTemplates.edges.map(n => n.node.id))
            this.setState({ selectAll: true })
        } else {
            this.props.setSelectedPricing([])
            this.setState({ selectAll: false })
        }
    };

    render() {
        return <PrimaryListingContainer listLoading={this.props.loading || this.state.loading} totalFetchedRows={this.props.allPricingTemplates && this.props.allPricingTemplates.edges.length} pageInfo={this.props.allPricingTemplates && this.props.allPricingTemplates.pageInfo} loadMoreEntries={this.props.allPricingTemplates && this.props.allPricingTemplates.pageInfo.hasNextPage ? this.props.loadMoreEntries : null} refetchListQuery={this.props.refetchQuery}>
            <Table responsive className="listing-table">
                <thead>
                    <tr>
                        <th onClick={this.toggleSelectAllClick} className="select-all-checkbox"> {this.state.selectAll ? <i className="fa fa-check-square-o" aria-hidden="true"></i> : <i className="fa fa-square-o" aria-hidden="true"></i>}</th>
                        {tableHeaders.map(header =>
                            header.sortable ?
                                <th key={header.id} id={header.id} style={{ cursor: "pointer" }} onClick={() => this.props.updateOrder(header.id)}>{this.props.orderBy.some(order => order.includes(header.id)) ? <i className={"fa " + (this.props.orderBy.includes(header.id) ? 'fa-sort-asc' : 'fa-sort-desc')}></i> : <i className='fa fa-sort'></i>}&nbsp;&nbsp;{header.name}</th> :
                                <th key={header.id} id={header.id}>{header.name}</th>
                        )}
                    </tr>
                </thead>
                <tbody>
                    {this.props.allPricingTemplates && this.props.allPricingTemplates.edges && this.props.allPricingTemplates.edges.length > 0 ? this.props.allPricingTemplates.edges.map((price, index) => {
                        const node = price.node
                        const branchName = node.agreementType?.branch?.name || node.branch?.name || "--"
                        const agreementName = node.agreementType ? node.agreementType.name : "All Agreement Types"
                        const mileageUnit = (node.agreementType?.branch?.defaultMileageUnit || node.branch?.defaultMileageUnit) === 'KM' ? 'KM' : 'MI'
                        const yearRange = `${node.carYearMin ?? "Any"} — ${node.carYearMax ?? "Any"}`
                        const mileageRange = mileageUnit === 'KM'
                            ? `${node.carMileageMinInKm != null ? parseFloat(node.carMileageMinInKm).toFixed(2) : "Any"} — ${node.carMileageMaxInKm != null ? parseFloat(node.carMileageMaxInKm).toFixed(2) : "Any"} KM`
                            : `${node.carMileageMin != null ? parseFloat(node.carMileageMin).toFixed(2) : "Any"} — ${node.carMileageMax != null ? parseFloat(node.carMileageMax).toFixed(2) : "Any"} MI`
                        const isExpanded = this.state.expandedRows.has(node.id)

                        return (
                            <tr key={node.id} id={node.id}>
                                <td scope="row" className="listing-checkbox-wrapper">
                                    {!this.props.selectedPricing.includes(node.id) && <span className="listing-counter">{index + 1}</span>}
                                    <span className="listing-checkbox" onClick={() => this.updateSelected(node.id)}>
                                        {this.props.selectedPricing.includes(node.id) ? <i className="fa fa-check-square-o" aria-hidden="true"></i> : <i className="fa fa-square-o unchecked" aria-hidden="true"></i>}
                                    </span>
                                </td>
                                <td>
                                    <span className="listing-identity__meta">
                                        <span title="Branch">{branchName}</span> — <span title="Agreement Type">{agreementName}</span>
                                    </span>
                                    <span className="listing-identity__title" title="Title">{node.title || "--"}</span>
                                </td>
                                <td>
                                    {node.carModel ? <>
                                        <span className="listing-vehicle__group" title="Car Group">{node.carModel.groupType ? node.carModel.groupType.name : "--"}</span>
                                        <span className="listing-vehicle__model">{toTitleCase(node.carModel.make || "") + " " + toTitleCase(node.carModel.name)}{node.carModel.series && <> <span className="listing-vehicle__series" title={node.carModel.series}>({node.carModel.series})</span></>}</span>
                                    </> : <span className="listing-vehicle__model">For All Models</span>}
                                    <button type="button" className={"listing-expand-toggle" + (isExpanded ? " is-open" : "")} onClick={() => this.toggleExpand(node.id)}>
                                        <i className={"fa " + (isExpanded ? "fa-angle-down" : "fa-angle-right")} aria-hidden="true"></i>
                                        Vehicle details
                                    </button>
                                    {isExpanded &&
                                        <div className="listing-expand-panel">
                                            <div className="listing-vehicle__fact">Year <b>{yearRange}</b></div>
                                            <div className="listing-vehicle__fact">Mileage <b>{mileageRange}</b></div>
                                            <div className="listing-vehicle__fact">Color <b>{node.carColor || "Any"}</b></div>
                                        </div>
                                    }
                                </td>
                                <td>
                                    <span className="listing-value">
                                        <span className="listing-value__amount">${node.price}</span>
                                        <span className="listing-value__meta">/ {node.interval} {node.intervalUnit}</span>
                                    </span>
                                    <span className="listing-value-range">Range ${node.minimumPrice} — ${node.maximumPrice}</span>
                                </td>
                                <td>
                                    <div className="listing-status-cluster">
                                        <StatusChip on={node.isDefault} onText="Default" offText="Not default" />
                                        <StatusChip on={node.isActive} onText="Active" offText="Inactive" />
                                    </div>
                                </td>
                                <td>
                                    <div className="listing-status-cluster">
                                        <VisibilityChip visible={node.visibleToCustomers} />
                                    </div>
                                </td>
                                <td>
                                    <i className={"fa fa-pencil fa-lg"} onClick={() => this.toggleModal("editPricing" + price.node.pk)} id={"editPricing" + price.node.pk}></i>
                                    {this.state.openModal === "editPricing" + price.node.pk &&
                                        <EditPricingModal
                                            target={"editPricing" + price.node.pk}
                                            isOpen={this.state.openModal === "editPricing" + price.node.pk}
                                            price={node}
                                            refetchAllPricingTemplates={() => this.props.refetchQuery()}
                                            handleClose={() => this.toggleModal("editPricing" + price.node.pk)}
                                        />
                                    }
                                </td>
                            </tr>
                        )
                    }) : !this.props.loading && <tr>
                        <td colSpan={20}><p className="column-text">No Templates Found!</p></td>
                    </tr>
                    }
                </tbody>
            </Table>
        </PrimaryListingContainer>
    }
}

const PricingTableWithData = graphql(AllPricingTemplates, {
    options: ({ searchTerm, orderBy, filters }) => ({
        variables: { first: 30, orderBy, searchTerm, filters },
        fetchPolicy: "cache-and-network",
    }), props: ({ data: { loading, allPricingTemplates, fetchMore, variables } }) => (
        {
            loading, allPricingTemplates, variables,
            refetchQuery: () => {
                let currentLength = allPricingTemplates && allPricingTemplates.edges ? allPricingTemplates.edges.length : 30
                return fetchMore({
                    query: AllPricingTemplates,
                    variables: {
                        ...variables,
                        first: currentLength,
                    },
                    updateQuery: (previousResult, { fetchMoreResult }) => {
                        const newEdges = fetchMoreResult.allPricingTemplates.edges
                        const pageInfo = fetchMoreResult.allPricingTemplates.pageInfo
                        return {
                            allPricingTemplates: {
                                edges: [...newEdges],
                                pageInfo,
                                searchTerm: previousResult.searchTerm,
                                __typename: previousResult.allPricingTemplates.__typename
                            },
                        }
                    },
                })
            },

            loadMoreEntries: () => {
                return fetchMore({
                    query: AllPricingTemplates,
                    variables: {
                        cursor: allPricingTemplates.pageInfo.endCursor,
                        ...variables,
                    },
                    updateQuery: (previousResult, { fetchMoreResult }) => {
                        const newEdges = fetchMoreResult.allPricingTemplates.edges
                        const pageInfo = fetchMoreResult.allPricingTemplates.pageInfo

                        return {
                            allPricingTemplates: {
                                edges: [
                                    ...previousResult.allPricingTemplates.edges,
                                    ...newEdges,
                                ],
                                pageInfo,
                                status: previousResult.status,
                                __typename: previousResult.allPricingTemplates.__typename,
                            },
                        }
                    },
                })
            },
        }
    )
})(PricingTable)

// The container owns the editable is_active preset (session state); advanced filters
// live in the URL. Combine both into the single `filters` variable the query sends.
export default function PricingTableWithFilters(props) {
    const [advancedFilters] = useAdvancedFilters()
    return <PricingTableWithData {...props} filters={combineFilters(props.presets, advancedFilters)} />
}
